import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { BrandColorName } from '@region13/shared-types'

export type CalloutTone = 'warn' | 'ok' | 'error' | 'info'

/** Wash + icon color per tone.
 *
 *  Hoisted out of the component and exported so the contrast guard in
 *  `tailwindPreset.test.ts` can measure the pairs a Callout really renders. The other
 *  half — that the component actually APPLIES what it declares here — is checked by
 *  Callout.test.tsx (2026-08-01); before the render harness existed, this constant was
 *  the only thing any test could see, so the two could silently disagree.
 *
 *  The classes are spelled out as literal strings and the tokens repeated
 *  alongside them, rather than the classes being built from the tokens: the
 *  Tailwind content scanner only sees literals, so a template-built
 *  `bg-brand-${wash}/10` would silently generate no CSS at all. The test pins
 *  the two halves together so the duplication cannot drift.
 *
 *  The icon carries the tone at a glance, so each one clears the 3:1 non-text
 *  threshold against its OWN wash. That is why warn is `gold-dark`: plain
 *  `brand-gold` on `brand-gold/10` measures 1.62:1 and is invisible. */
export const CALLOUT_TONES: Record<
  CalloutTone,
  { bg: string; icon: string; washToken: BrandColorName; washAlpha: number; iconToken: BrandColorName }
> = {
  warn:  { bg: 'bg-brand-gold/10',      icon: 'text-brand-gold-dark',  washToken: 'gold',     washAlpha: 0.1,  iconToken: 'gold-dark' },
  ok:    { bg: 'bg-brand-green/10',     icon: 'text-brand-green-dark', washToken: 'green',    washAlpha: 0.1,  iconToken: 'green-dark' },
  error: { bg: 'bg-brand-red-dark/10',  icon: 'text-brand-red-dark',   washToken: 'red-dark', washAlpha: 0.1,  iconToken: 'red-dark' },
  info:  { bg: 'bg-brand-maroon/5',     icon: 'text-brand-maroon',     washToken: 'maroon',   washAlpha: 0.05, iconToken: 'maroon' },
}

/** Tinted callout — background wash + leading icon, no side stripe
 *  (the border-left accent stripe is a banned AI tell). */
export function Callout({
  tone,
  icon: Icon,
  title,
  children,
  className = '',
  role,
}: {
  tone: CalloutTone
  icon: LucideIcon
  title?: ReactNode
  children?: ReactNode
  className?: string
  /** ARIA live role — 'alert' for errors, 'status' for success/info. */
  role?: 'alert' | 'status'
}) {
  const { bg, icon: iconColor } = CALLOUT_TONES[tone]
  return (
    <div role={role} className={`flex items-start gap-2.5 rounded-md px-3 py-2.5 text-sm text-ink ${bg} ${className}`}>
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${iconColor}`} aria-hidden="true" />
      <div className="min-w-0">
        {title ? <div className="font-semibold">{title}</div> : null}
        {children}
      </div>
    </div>
  )
}
